import React, { useEffect, useRef, useState } from "react";
import "./css/MenuDemo.css";
import { Link } from "react-router-dom";

const categories = [
    { id: "entradas", label: "Entradas", icon: "🥗" },
    { id: "principais", label: "Pratos Principais", icon: "🍽️" },
    { id: "bebidas", label: "Bebidas", icon: "🥤" },
    { id: "sobremesas", label: "Sobremesas", icon: "🍰" },
];

const menuItems = {
    entradas: [
        {
            id: 1,
            name: "Bruschetta Clássica",
            description: "Pão italiano tostado, tomate confit, manjericão fresco e azeite trufado.",
            price: "R$ 28,00",
            tag: "Mais pedido",
        },
        {
            id: 2,
            name: "Carpaccio de Filé",
            description: "Fatias finas de filé mignon, alcaparras, rúcula e lascas de parmesão.",
            price: "R$ 42,00",
        },
        {
            id: 3,
            name: "Bolinho de Bacalhau",
            description: "6 unidades, receita tradicional portuguesa, servido com molho tártaro.",
            price: "R$ 34,00",
        },
    ],
    principais: [
        {
            id: 4,
            name: "Risoto de Camarão",
            description: "Arroz arbóreo cremoso, camarões grelhados, tomate cereja e ervas finas.",
            price: "R$ 68,00",
            tag: "Mais pedido",
        },
        {
            id: 5,
            name: "Filé ao Molho Madeira",
            description: "Filé mignon grelhado ao ponto, molho madeira, purê de batatas e legumes salteados.",
            price: "R$ 74,00",
        },
        {
            id: 6,
            name: "Nhoque de Batata",
            description: "Nhoque artesanal ao molho sugo, manjericão e queijo parmesão ralado na hora.",
            price: "R$ 48,00",
            tag: "Vegetariano",
        },
        {
            id: 7,
            name: "Salmão Grelhado",
            description: "Filé de salmão grelhado, aspargos, purê de mandioquinha e molho de maracujá.",
            price: "R$ 79,00",
        },
    ],
    bebidas: [
        {
            id: 8,
            name: "Suco Natural",
            description: "Laranja, abacaxi com hortelã ou limão. Escolha o seu.",
            price: "R$ 14,00",
        },
        {
            id: 9,
            name: "Água com ou sem Gás",
            description: "500ml, gelada.",
            price: "R$ 8,00",
        },
        {
            id: 10,
            name: "Taça de Vinho",
            description: "Tinto, branco ou rosé. Consulte o sommelier para harmonização.",
            price: "R$ 32,00",
        },
        {
            id: 11,
            name: "Refrigerante",
            description: "Lata 350ml — diversos sabores.",
            price: "R$ 9,00",
        },
    ],
    sobremesas: [
        {
            id: 12,
            name: "Petit Gâteau",
            description: "Bolo quente de chocolate com recheio cremoso e sorvete de creme.",
            price: "R$ 32,00",
            tag: "Mais pedido",
        },
        {
            id: 13,
            name: "Tiramisù",
            description: "Receita italiana tradicional com café espresso e mascarpone.",
            price: "R$ 29,00",
        },
        {
            id: 14,
            name: "Cheesecake de Frutas Vermelhas",
            description: "Base amanteigada, cream cheese e calda artesanal de frutas vermelhas.",
            price: "R$ 30,00",
        },
    ],
};

const MenuDemo = () => {
    const [activeCategory, setActiveCategory] = useState(categories[0].id);
    const sectionRefs = useRef({});

    const scrollToCategory = (id) => {
        setActiveCategory(id);
        sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveCategory(entry.target.dataset.category);
                    }
                });
            },
            { rootMargin: "-140px 0px -60% 0px" }
        );

        Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div className="menu-demo-page">
            <div className="menu-demo-tag">
                <i className="bi bi-qr-code"></i> Exemplo de Cardápio Digital — Demonstração FN Studio
            </div>

            <header className="menu-demo-header">
                <div className="menu-demo-cover">
                    <div className="menu-demo-logo">🍷</div>
                </div>
                <h1 className="menu-demo-name">Cantina Bella Vista</h1>
                <p className="menu-demo-tagline">Cozinha italiana contemporânea</p>
                <div className="menu-demo-meta">
                    <span><i className="bi bi-star-fill"></i> 4.8 (312 avaliações)</span>
                    <span><i className="bi bi-geo-alt-fill"></i> Rua das Flores, 123</span>
                    <span><i className="bi bi-clock-fill"></i> Aberto até 23h</span>
                </div>
            </header>

            <nav className="menu-demo-nav">
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        className={`menu-demo-nav-item ${activeCategory === cat.id ? "active" : ""}`}
                        onClick={() => scrollToCategory(cat.id)}
                    >
                        <span className="menu-demo-nav-icon">{cat.icon}</span>
                        {cat.label}
                    </button>
                ))}
            </nav>

            <main className="menu-demo-content">
                {categories.map((cat) => (
                    <section
                        key={cat.id}
                        id={cat.id}
                        data-category={cat.id}
                        ref={(el) => (sectionRefs.current[cat.id] = el)}
                        className="menu-demo-category"
                    >
                        <h2 className="menu-demo-category-title">
                            <span>{cat.icon}</span> {cat.label}
                        </h2>

                        <div className="menu-demo-items">
                            {menuItems[cat.id].map((item) => (
                                <div className="menu-demo-item" key={item.id}>
                                    <div className="menu-demo-item-info">
                                        <div className="menu-demo-item-header">
                                            <h3>{item.name}</h3>
                                            {item.tag && <span className="menu-demo-item-tag">{item.tag}</span>}
                                        </div>
                                        <p className="menu-demo-item-description">{item.description}</p>
                                    </div>
                                    <div className="menu-demo-item-price">{item.price}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            <footer className="menu-demo-footer">
                <p>Peça diretamente ao garçom. Cardápio sujeito a alterações sem aviso prévio.</p>

                <div className="menu-demo-cta-card">
                    <h3>Gostou deste cardápio?</h3>
                    <p>
                        Este é um exemplo de cardápio digital via QR Code ou NFC que a FN Studio pode
                        criar para o seu restaurante, bar ou cafeteria — sem app, sem papel, sempre atualizado.
                    </p>
                    <Link to="/services" className="menu-demo-cta-button">Quero um cardápio assim →</Link>
                </div>
            </footer>
        </div>
    );
};

export default MenuDemo;
